'use client';

import { Flex } from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';

import SearchInput from '@components/common/SearchInput';
import AssistantSurface from '@features/assistant/AssistantSurface';
import { DEMAND_ASSISTANT_CONFIG } from '@features/assistant/modeSuggestions';
import BusinessPartnerGrid from '@features/supply/BusinessPartnerGrid';
import { allBusinessPartners, Sector } from '@features/supply/fakeSupplyChainData';
import PoweredByDatanaErp from '@features/supply/PoweredByDatanaErp';
import SectorCards from '@features/supply/SectorCards';
import SectorFilter from '@features/supply/SectorFilter';

const EMPTY_SET = new Set<Sector>();

const SupplyChainsPage = () => {
  const [selectedSectors, setSelectedSectors] = useState<Set<Sector>>(EMPTY_SET);
  const [searchQuery, setSearchQuery] = useState('');

  const handleToggleSector = useCallback((sectorId: Sector) => {
    setSelectedSectors((prev) => {
      const next = new Set(prev);
      if (next.has(sectorId)) {
        next.delete(sectorId);
      } else {
        next.add(sectorId);
      }
      return next;
    });
  }, []);

  const handleClearAll = useCallback(() => {
    setSelectedSectors(EMPTY_SET);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
  }, []);

  const filteredPartners = useMemo(() => {
    let partners = allBusinessPartners;
    if (selectedSectors.size > 0) {
      partners = partners.filter((bp) => selectedSectors.has(bp.sector));
    }
    const trimmed = searchQuery.trim();
    if (trimmed !== '') {
      const query = trimmed.toLowerCase();
      partners = partners.filter((bp) => bp.name.toLowerCase().includes(query));
    }
    return partners;
  }, [selectedSectors, searchQuery]);

  return (
    <AssistantSurface config={DEMAND_ASSISTANT_CONFIG}>
      <Flex direction="column" p={4} gap={6}>
        <Flex justifyContent="flex-end">
          <PoweredByDatanaErp />
        </Flex>
        <SectorCards />
        <Flex gap={3} alignItems="center" flexWrap="wrap">
          <SectorFilter
            selectedSectors={selectedSectors}
            onToggleSector={handleToggleSector}
            onClearAll={handleClearAll}
          />
          <SearchInput
            query={searchQuery}
            setQuery={setSearchQuery}
            onClear={handleClearSearch}
            placeholder="Search partners"
            maxW="300px"
            size="sm"
          />
        </Flex>
        <BusinessPartnerGrid partners={filteredPartners} />
      </Flex>
    </AssistantSurface>
  );
};

export default SupplyChainsPage;
